import wrapperHOC from "@Utils/wrapperHOC";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import styles from "./FeaturedBlogSection.module.scss";
import Image from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import ChevronRightSmallIcon from "icons/ChevronRightSmallIcon";
import useIsDesktop from "hooks/useIsDesktop";

export interface FeaturedBlogItem {
  id: number;
  name: string;
  slug: string;
  mainImage?: {
    url?: string;
    alt?: string;
  };
  category?: {
    name?: string;
  };
  readTime?: string;
  date?: string;
  updatedAt?: string;
}

interface FeaturedBlogSectionProps {
  blogs: FeaturedBlogItem[];
  basePath: string;
  title?: string;
  subtitle?: string;
  currentBlogSlug?: string;
  className?: string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const FeaturedBlogSection = ({
  blogs,
  basePath,
  title,
  subtitle,
  currentBlogSlug,
  className,
}: FeaturedBlogSectionProps) => {
  const isDesktop = useIsDesktop();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const getCardWidth = useCallback(() => {
    if (!scrollRef.current) return 0;
    const firstCard = scrollRef.current.querySelector(`.${styles.blogCard}`) as HTMLElement;
    if (!firstCard) return 0;
    const style = window.getComputedStyle(scrollRef.current);
    const gap = parseFloat(style.columnGap) || 0;
    return firstCard.offsetWidth + gap;
  }, []);

  const updateScrollState = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const scrollNext = useCallback(() => {
    if (!scrollRef.current) return;
    const cardWidth = getCardWidth();
    if (cardWidth === 0) return;
    scrollRef.current.scrollTo({
      left: scrollRef.current.scrollLeft + cardWidth,
      behavior: "smooth",
    });
  }, [getCardWidth]);

  const scrollPrev = useCallback(() => {
    if (!scrollRef.current) return;
    const cardWidth = getCardWidth();
    if (cardWidth === 0) return;
    scrollRef.current.scrollTo({
      left: scrollRef.current.scrollLeft - cardWidth,
      behavior: "smooth",
    });
  }, [getCardWidth]);

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  if (!blogs || blogs.length < 3) return null;

  const sectionTitle = title || "Latest Blogs";
  const sectionSubtitle = subtitle || "If you loved this, here\u0027s what else you\u0027ll enjoy";

  const filteredBlogs = currentBlogSlug
    ? blogs.filter((blog) => blog.slug !== currentBlogSlug)
    : blogs;

  if (filteredBlogs.length < 3) return null;

  const displayBlogs = filteredBlogs.slice(0, isDesktop ? 10 : 5);

  return (
    <section className={`${styles.featuredSection}${className ? ` ${className}` : ""}`}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerLeft}>
          <h2 className={styles.sectionTitle}>{sectionTitle}</h2>
          <p className={styles.sectionSubtitle}>{sectionSubtitle}</p>
        </div>
      </div>

      <div className={styles.carouselWrapper}>
        {canScrollLeft && (
          <button
            className={`${styles.navArrow} ${styles.navArrowLeft}`}
            onClick={scrollPrev}
            aria-label="Previous"
            type="button"
          >
            <ChevronRightSmallIcon width={16} height={16} color="#111928" />
          </button>
        )}

        <div ref={scrollRef} className={styles.featuredGrid} onScroll={updateScrollState}>
          {displayBlogs.map((blog) => {
            const imageUrl = blog.mainImage?.url || "";
            const imageAlt = blog.mainImage?.alt || blog.name;
            const categoryName = blog.category?.name || "Article";
            const readTime = blog.readTime || "5 Min";
            const blogDate = blog.updatedAt || blog.date || "";

            return (
              <CustomLink
                key={blog.id}
                to={`${basePath}/post/${blog.slug}`}
                redirect
                skipLangPrefix
                className={styles.blogCard}
              >
                <div className={styles.cardImageContainer}>
                  <Image
                    src={`${imageUrl}?w=500`}
                    alt={imageAlt}
                    width="100%"
                    height={isDesktop ? "171" : "95"}
                    className={styles.cardImage}
                  />
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.metaRow}>
                    <span className={styles.categoryBadge}>{categoryName}</span>
                    <div className={styles.metaInfo}>
                      <span className={styles.readTime}>{readTime}</span>
                      <span className={styles.separator}>&middot;</span>
                      <span className={styles.date}>{blogDate ? formatDate(blogDate) : ""}</span>
                    </div>
                  </div>
                  <p className={styles.cardTitle}>{blog.name}</p>
                </div>
              </CustomLink>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            className={`${styles.navArrow} ${styles.navArrowRight}`}
            onClick={scrollNext}
            aria-label="Next"
            type="button"
          >
            <ChevronRightSmallIcon width={16} height={16} color="#111928" />
          </button>
        )}
      </div>
    </section>
  );
};

export default wrapperHOC(memo(FeaturedBlogSection), {
  componentName: "FeaturedBlogSection-Blog",
  showForChina: false,
});
